import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import useArgument from './useArgument';
import ArgumentView from './components/ArgumentView';
import PageContainer from './components/PageContainer';

import { Box, Typography, TextField, Button } from '@mui/material';

const ArgumentPage: React.FC = () => {
  const { uid, originatorName, topic } = useParams<{
    uid: string;
    originatorName: string;
    topic: string;
  }>();

  if (!uid) {
    throw new Error('UID is required to load this page.');
  }

  const [message, setMessage] = useState('');
  const [userName, setUserName] = useState('');
  const [stage, setStage] = useState<'name' | 'argument'>('name');

  const argumentHook = useArgument({ uid, userName });

  // Handle transitioning to the argument stage
  const handleNameSubmit = () => {
    if (userName.trim() !== '') {
      setStage('argument');
    } else {
      setMessage('Please enter your name.');
    }
  };

  // Page subtitle based on stage
  const subtitle =
    stage === 'name'
      ? `${decodeURIComponent(originatorName!)} has challenged you to an argument about ${decodeURIComponent(topic!)}`
      : `Arguing with ${decodeURIComponent(originatorName!)} about ${decodeURIComponent(topic!)}`;

  return (
    <PageContainer subtitle={subtitle}>
      <Typography variant="subtitle1">
        Quick, fair resolutions to any disagreement.
      </Typography>

      {/* Name Stage */}
      {stage === 'name' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 4 }}>
          <TextField
            label="Your Name"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            variant="outlined"
            fullWidth
            sx={{ maxWidth: 600 }}
          />
          <Button
            onClick={handleNameSubmit}
            variant="contained"
            color="primary"
          >
            Submit Name
          </Button>
        </Box>
      )}

      {/* Argument Stage */}
      {stage === 'argument' && (
        <ArgumentView
          argumentHook={argumentHook}
          opponentName={argumentHook.opponentName}
          opponentState={argumentHook.opponentState}
          topic={decodeURIComponent(topic!)}
        />
      )}

      {/* Error or status messages */}
      {message && (
        <Typography color="error" sx={{ mt: 2 }}>
          {message}
        </Typography>
      )}
    </PageContainer>
  );
};

export default ArgumentPage;
